import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import {
  HospitalMasterDTO,
  MenuMasterCreateRequestDTO,
  MenuMasterDeleteRequestDTO,
  MenuMasterResponseDTO,
  MenuMasterUpdateRequestDTO,
} from "../hospital-master.interface";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HospitalMasterService } from "../hospital-master.service";

@Component({
  selector: "app-menu-master",
  templateUrl: "./menu-master.component.html",
  styleUrls: ["./menu-master.component.css"],
})
export class MenuMasterComponent implements OnInit, OnDestroy {
  @Input() hospitalInfo!: HospitalMasterDTO;
  @Input() userWindow!: number;

  menus: MenuMasterResponseDTO[] = [];
  filteredMenus: MenuMasterResponseDTO[] = [];

  // Form & Modal
  menuForm: FormGroup;
  showModal = false;
  showDeleteModal = false;
  modalMode: "create" | "edit" = "create";

  // State management
  loading = false;
  submitting = false;
  searchQuery = "";
  selectedMenu: MenuMasterResponseDTO | null = null;

  // Messages
  successMessage = "";
  errorMessage = "";
  showSuccessMessage = false;
  pageSize: number =5;
currentPage: number = 1;
pageSizeOptions: number[] = [5, 10, 20, 50];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private menuService: HospitalMasterService,
  ) {
    this.menuForm = this.fb.group({
      menuId: [null],
      menuName: [
  "",
  [
    Validators.required,
    Validators.minLength(3),
    Validators.pattern(/.*\S.*/)
  ]
],
      menuCode: ["", [Validators.required, Validators.minLength(2)]],
      menuDesc: [""],
      displayOrder: [0, [Validators.required, Validators.min(0)]],
      parentMenuId: [0],
      defaultChildMenuId: [0],
      menuIconUrl: [""],
      templatePath: [""],
      isActive: [true],
    });
  }

  ngOnInit(): void {
    this.loadMenus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  get totalPages(): number {
  return Math.ceil((this.filteredMenus?.length || 0) / this.pageSize) || 1;
}

get paginatedMenus(): any[] {
  const startIndex = (this.currentPage - 1) * this.pageSize;
  const endIndex = startIndex + this.pageSize;
  return this.filteredMenus.slice(startIndex, endIndex);
}

get paginationStart(): number {
  if (!this.filteredMenus.length) return 0;
  return (this.currentPage - 1) * this.pageSize + 1;
}

get paginationEnd(): number {
  return Math.min(this.currentPage * this.pageSize, this.filteredMenus.length);
}

get pageNumbers(): number[] {
  return Array.from({ length: this.totalPages }, (_, i) => i + 1);
}

get visiblePageNumbers(): number[] {
  const total = this.totalPages;
  const current = this.currentPage;

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 4) {
    return [1, 2, 3, 4, 5, -1, total];
  }

  if (current >= total - 3) {
    return [1, -1, total - 4, total - 3, total - 2, total - 1, total];
  }

  return [1, -1, current - 1, current, current + 1, -1, total];
}

goToPage(page: number): void {
  if (page < 1 || page > this.totalPages) return;
  this.currentPage = page;
}

previousPage(): void {
  if (this.currentPage > 1) {
    this.currentPage--;
  }
}

nextPage(): void {
  if (this.currentPage < this.totalPages) {
    this.currentPage++;
  }
}

onPageSizeChange(event: Event): void {
  const value = Number((event.target as HTMLSelectElement).value);
  this.pageSize = value;
  this.currentPage = 1;
}

loadMenus(): void {
  this.loading = true;
  this.errorMessage = "";

  this.menuService.MenuMaster_readAll().subscribe({
    next: (res: any) => {

      this.menus = (res?.items || [])
        .filter((x: any) => Number(x.isDeleted || 0) === 0)
        .map((x: any) => ({
          menuId: Number(x.menuId || 0),
          menuName: x.menuName || "",
          menuCode: x.menuCode || "",
          menuDesc: x.menuDesc || "",
          displayOrder: Number(x.displayOrder || 0),
          parentMenuId: Number(x.parentMenuId || 0),
          defaultChildMenuId: Number(x.defaultChildMenuId || 0),
          menuIconUrl: x.menuIconUrl || "",
          templatePath: x.templatePath || "",
          isActive: Number(x.isActive || 0),
          createdBy: x.createdBy || "",
          createdOn: x.createdOn || null,
          modifiedBy: x.modifiedBy || "",
          modifiedOn:
            x.modifiedOn && x.modifiedOn !== "0001-01-01T00:00:00"
              ? x.modifiedOn
              : null,
          isDeleted: Number(x.isDeleted || 0),
        }));

      this.filteredMenus = [...this.menus];


      this.loading = false;
    },
    error: (err) => {
      console.error("Menu ReadAll Error:", err);
      this.errorMessage = "Failed to load menus";
      this.loading = false;
    },
  });
}

  openCreateModal(): void {
    this.modalMode = "create";
    this.menuForm.reset({
      isActive: true,
      displayOrder: 0,
      parentMenuId: 0,
      defaultChildMenuId: 0,
    });
    this.showModal = true;
    this.errorMessage = '';
  }

  openEditModal(menu: MenuMasterResponseDTO): void {
    this.errorMessage = '';
    this.modalMode = "edit";
    this.selectedMenu = menu;
    this.menuForm.patchValue({
      menuId: menu.menuId,
      menuName: menu.menuName,
      menuCode: menu.menuCode,
      menuDesc: menu.menuDesc,
      displayOrder: menu.displayOrder,
      parentMenuId: menu.parentMenuId,
      defaultChildMenuId: menu.defaultChildMenuId,
      menuIconUrl: menu.menuIconUrl,
      templatePath: menu.templatePath,
      isActive: menu.isActive,
    });
    this.showModal = true;
  }

  openDeleteModal(menu: MenuMasterResponseDTO): void {
    this.selectedMenu = menu;
    this.showDeleteModal = true;
    this.errorMessage = '';
  }

  closeModal(): void {
    this.showModal = false;
    this.menuForm.reset({ isActive: true });
    this.selectedMenu = null;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedMenu = null;
  }

  submitForm(): void {
    if (!this.menuForm.valid) {
      this.errorMessage = "Please fill in all required fields correctly.";
      return;
    }

    this.submitting = true;
    this.errorMessage = "";

    if (this.modalMode === "create") {
      this.createMenu();
    } else {
      this.updateMenu();
    }
  }

  createMenu(): void {
    const formData = this.menuForm.value;
    const request: MenuMasterCreateRequestDTO = {
      menuName: formData.menuName,
      menuCode: formData.menuCode,
      menuDesc: formData.menuDesc || "",
      displayOrder: formData.displayOrder,
      parentMenuId: formData.parentMenuId,
      defaultChildMenuId: formData.defaultChildMenuId,
      menuIconUrl: formData.menuIconUrl || "",
      templatePath: formData.templatePath || "",
      actionUser: "SYSTEM", // Replace with actual user
    };

    this.menuService.MenuMaster_create(request).subscribe({
      next: (res) => {
        this.successMessage = "Menu created successfully!";
        this.showSuccessMessage = true;
        this.submitting = false;
        this.closeModal();
        this.loadMenus();
        this.hideSuccessMessage();
      },
      error:(err)=> {
          console.error(err);
  this.errorMessage =
    err?.error?.message || "Failed to create menu";
  this.submitting = false;
      },
    });
  }

  updateMenu(): void {
    const formData = this.menuForm.value;
    const request: MenuMasterUpdateRequestDTO = {
      menuId: formData.menuId,
      menuName: formData.menuName,
      menuCode: formData.menuCode,
      menuDesc: formData.menuDesc,
      displayOrder: formData.displayOrder,
      parentMenuId: formData.parentMenuId,
      defaultChildMenuId: formData.defaultChildMenuId,
      menuIconUrl: formData.menuIconUrl,
      templatePath: formData.templatePath,
      isActive: formData.isActive ? 1 : 0,
      actionUser: "SYSTEM", // Replace with actual user
    };

    this.menuService.MenuMaster_update(request).subscribe({
      next: (res) => {
        this.successMessage = "Menu updated successfully!";
        this.showSuccessMessage = true;
        this.submitting = false;
        this.closeModal();
        this.loadMenus();
        this.hideSuccessMessage();
      },
     error: (err) => {
  console.error(err);
  this.errorMessage =
    err?.error?.message || "Failed to update menu";
  this.submitting = false;
},
    });
  }

  deleteMenu(): void {
    if (!this.selectedMenu) return;

    this.submitting = true;
    const request: MenuMasterDeleteRequestDTO = {
      menuId: this.selectedMenu.menuId,
      actionUser: "SYSTEM", // Replace with actual user
    };

    this.menuService.MenuMaster_delete(request).subscribe({
      next: (res) => {
        this.successMessage = "Menu deleted successfully!";
        this.showSuccessMessage = true;
        this.submitting = false;
        this.closeDeleteModal();
        this.loadMenus();
        this.hideSuccessMessage();
      },
error: (err) => {
  console.error(err);
  this.errorMessage =
    err?.error?.message || "Failed to delete menu";
  this.submitting = false;
},
    });
  }

onSearch(value: string): void {
  this.searchQuery = value;

  const query = value.toLowerCase().trim();

  if (!query) {
    this.filteredMenus = [...this.menus];
  } else {
    this.filteredMenus = this.menus.filter((menu: any) =>
      (menu.menuName || "").toLowerCase().includes(query) ||
      (menu.menuCode || "").toLowerCase().includes(query) ||
      (menu.menuDesc || "").toLowerCase().includes(query)
    );
  }

  this.currentPage = 1;
}

  applySearch(): void {
    if (!this.searchQuery.trim()) {
      this.filteredMenus = [...this.menus];
      return;
    }

    const query = this.searchQuery.toLowerCase();
this.filteredMenus = this.menus.filter(
  (menu) =>
    (menu.menuName || '').toLowerCase().includes(query) ||
    (menu.menuCode || '').toLowerCase().includes(query) ||
    (menu.menuDesc || '').toLowerCase().includes(query)
);
  }

  private hideSuccessMessage(): void {
    setTimeout(() => {
      this.showSuccessMessage = false;
    }, 4000);
  }

  get isFormValid(): boolean {
    return this.menuForm.valid;
  }

  getStatusClass(isActive: boolean): string {
    return isActive ? "active" : "inactive";
  }

  getStatusText(isActive: boolean): string {
    return isActive ? "Active" : "Inactive";
  }
}
